"use client";

import { Button } from "@/components/ui/button";
import { Message } from "@/lib/core/crm/types";
import { ContactPopover } from "@/features/contacts/components/ContactPopover";
import { User, Calendar, Paperclip, Download, Loader2 } from "lucide-react";
import { useState, useCallback } from "react";

interface MessageMetadataProps {
  message: Message;
}

interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
}

export function MessageMetadata({ message }: MessageMetadataProps) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString([], {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchAttachments = useCallback(async () => {
    if (attachments.length > 0 || loadingAttachments) return;

    setLoadingAttachments(true);
    try {
      const response = await fetch(
        `/api/debug/msft-emails/attachments?messageId=${encodeURIComponent(message.providerMessageId)}`
      );

      if (!response.ok) {
        const error = await response.json();
        console.error("Failed to fetch attachments:", error);
        return;
      }

      const data = await response.json();
      setAttachments(data.attachments || []);
      setShowAttachments(true);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    } finally {
      setLoadingAttachments(false);
    }
  }, [message.providerMessageId, attachments.length, loadingAttachments]);

  const downloadAttachment = useCallback(
    async (attachmentId: string, fileName: string) => {
      setDownloadingId(attachmentId);
      try {
        const response = await fetch(
          `/api/debug/msft-emails/download?messageId=${encodeURIComponent(
            message.providerMessageId
          )}&attachmentId=${encodeURIComponent(attachmentId)}`
        );

        if (!response.ok) {
          console.error("Failed to download attachment");
          return;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error("Error downloading attachment:", error);
      } finally {
        setDownloadingId(null);
      }
    },
    [message.providerMessageId]
  );

  // Defensive checks for sender data
  const senderName = message?.sender?.name || "Unknown Sender";
  const senderEmail = message?.sender?.email || "";
  const recipients = message?.recipients || [];
  const ccRecipients = message?.ccRecipients || [];

  return (
    <div className="border-b border-border mb-4 pb-4">
      <div className="space-y-3">
        {/* From */}
        <div className="flex items-start space-x-3">
          <User className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium">From:</div>
            <div className="text-sm">
              <ContactPopover name={senderName} email={senderEmail}>
                <span className="cursor-pointer hover:underline">
                  {senderName} &lt;{senderEmail}&gt;
                </span>
              </ContactPopover>
            </div>
          </div>
        </div>

        {/* To */}
        {recipients.length > 0 ? (
          <div className="flex items-start space-x-3">
            <User className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium">To:</div>
              <div className="text-sm">
                {recipients.map((r, index) => (
                  <ContactPopover key={index} name={r.name || "Unknown"} email={r.email || ""}>
                    <span className="cursor-pointer hover:underline">
                      {r.name || "Unknown"} &lt;{r.email || ""}&gt;
                      {index < recipients.length - 1 && ", "}
                    </span>
                  </ContactPopover>
                ))}
              </div>
            </div>
          </div>
        ) : ccRecipients.length === 0 ? (
          <div className="flex items-start space-x-3">
            <User className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium">To:</div>
              <div className="text-sm">
                <span className="text-muted-foreground">No recipients</span>
              </div>
            </div>
          </div>
        ) : null}

        {ccRecipients.length > 0 && (
          <div className="flex items-start space-x-3">
            <User className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium">Cc:</div>
              <div className="text-sm">
                {ccRecipients.map((r, index) => (
                  <ContactPopover key={`cc-${index}`} name={r.name || "Unknown"} email={r.email || ""}>
                    <span className="cursor-pointer hover:underline">
                      {r.name || "Unknown"} &lt;{r.email || ""}&gt;
                      {index < ccRecipients.length - 1 && ", "}
                    </span>
                  </ContactPopover>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Date */}
        <div className="flex items-start space-x-3">
          <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium">Date:</div>
            <div className="text-sm">
              {message?.receivedDateTime ? formatDate(message.receivedDateTime) : "Unknown date"}
            </div>
          </div>
        </div>

        {/* Attachments */}
        {message?.hasAttachments && (
          <div className="flex items-start space-x-3">
            <Paperclip className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium">Attachments:</div>
              <div className="space-y-2">
                {!showAttachments ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={fetchAttachments}
                    disabled={loadingAttachments}
                  >
                    {loadingAttachments ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Download className="h-3 w-3 mr-1" />
                        Show Attachments
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-1">
                    {attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center space-x-2 text-sm">
                        <Paperclip className="h-3 w-3 text-muted-foreground" />
                        <span className="flex-1 truncate">{attachment.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => downloadAttachment(attachment.id, attachment.name)}
                          disabled={downloadingId === attachment.id}
                          title="Download attachment"
                        >
                          {downloadingId === attachment.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
